'use client';

import React from 'react';
import {AlertTriangle, CheckCircle, Info, XCircle} from 'lucide-react';

import {Button} from '@/components/ui/button';
import {cn} from '@/libs/utils';
import type {ConfirmationModalProps} from '@/presentation/stores/ModalStore';

interface ConfirmationModalComponentProps extends ConfirmationModalProps {
    onClose?: () => void;
    modalId?: string;
}

const variantConfig = {
    default: {
        icon: Info,
        iconColor: 'text-blue-500',
        confirmClass: 'bg-blue-600 hover:bg-blue-700',
    },
    destructive: {
        icon: XCircle,
        iconColor: 'text-red-500',
        confirmClass: 'bg-red-600 hover:bg-red-700',
    },
    success: {
        icon: CheckCircle,
        iconColor: 'text-green-500',
        confirmClass: 'bg-green-600 hover:bg-green-700',
    },
    warning: {
        icon: AlertTriangle,
        iconColor: 'text-yellow-500',
        confirmClass: 'bg-yellow-600 hover:bg-yellow-700',
    },
};

export const ConfirmationModal: React.FC<ConfirmationModalComponentProps> = ({
                                                                                 title,
                                                                                 message,
                                                                                 confirmText = 'Confirm',
                                                                                 cancelText = 'Cancel',
                                                                                 variant = 'default',
                                                                                 onConfirm,
                                                                                 onCancel,
                                                                                 onClose,
                                                                             }) => {
    const config = variantConfig[variant];
    const IconComponent = config.icon;

    const handleConfirm = async () => {
        try {
            if (onConfirm) {
                await onConfirm();
            }
        } finally {
            onClose?.();
        }
    };

    const handleCancel = () => {
        onCancel?.();
        onClose?.();
    };

    return (
        <div className="p-6">
            {/* Header */}
            <div className="flex items-center gap-3 mb-4">
                <IconComponent className={cn('h-6 w-6', config.iconColor)}/>
                <h2 className="text-lg font-semibold text-gray-900">
                    {title}
                </h2>
            </div>

            {/* Content */}
            <div className="mb-6">
                {typeof message === 'string' ? (
                    <p className="text-gray-700 leading-relaxed">
                        {message}
                    </p>
                ) : (
                    message
                )}
            </div>

            {/* Actions */}
            <div className="flex gap-3 justify-end">
                <Button
                    variant="outline"
                    onClick={handleCancel}
                    className="px-4 py-2"
                >
                    {cancelText}
                </Button>
                <Button
                    onClick={handleConfirm}
                    className={cn('px-4 py-2 text-white', config.confirmClass)}
                >
                    {confirmText}
                </Button>
            </div>
        </div>
    );
};

ConfirmationModal.displayName = 'ConfirmationModal';

export default ConfirmationModal;